import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@kufox/mui/Box';
import Drawer from '@kufox/mui/Drawer';
import { CloseOutlined } from '@kufox/icons';
import remoteEvent from '@tools/remoteEvent';
import uniqueModel from '@tools/uniqueModel';
import { extendModel } from '@utils';
import ThemeProvider from '@kufox/mui/ThemeProvider';
import useResponsive from '@kufox/mui/hooks/useResponsive';
import _ from 'lodash';
import useConstructor from '@hooks/useConstructor';
import loginModel from './model';
import ValidateForm from './ValidateForm';
import MailAuthorize from './MailAuthorize';
import { NAMESPACE } from './constants';
import { ga } from '../common/tools';
import RelLogin from './Login';
import Layout from '../Layout';

const { withLayout } = Layout;

const Login = (props = {}) => {
  useConstructor(() => {
    remoteEvent.emit(remoteEvent.evts.GET_DVA, (dva, m) => {
      uniqueModel(dva, extendModel(m, loginModel));
    });
  });
  const {
    onSuccess = _.noop,
    onForgetPwdClick = _.noop,
    BoxProps = {},
    verifyCanNotUseClick,
    showLoginSafeWord = false,
    signOrDownClick,
    loginKey,
    relLoginClasses,
    validateFormClasses,
    trackingConfig,
    theme,
    customTitle,
  } = props;

  const dispatch = useDispatch();
  const { type = 1, needValidations, isShowMailAuthorizePage } = useSelector(
    (state) => state[NAMESPACE],
  );
  const isSub = type === 3;
  const needValidate = needValidations.length > 0;
  const rv = useResponsive();

  useEffect(() => {
    pageVisitGa();
    eleExposureGa();
    return () => {
      dispatch({
        type: `${NAMESPACE}/reset`,
      });
    };
  }, []);

  // 页面访问事件集合
  const pageVisitGa = () => {
    ga('pageVisitCollectionV1', {
      siteid: 'KucoinWeb',
      siteid_pagecate: 'KucoinWeb_K0001',
      siteid_pagecate_pageid: 'KucoinWeb_K0001_Login',
    });
  };

  // 坑位曝光事件集合
  const eleExposureGa = () => {
    // 手机
    ga('eleExposureCollectionV1', {
      str1: 'KucoinWeb',
      str2: 'KucoinWeb.login',
      str3: 'KucoinWeb.login.SwitchButton',
      str4: 'KucoinWeb.login.SwitchButton.1',
    });
    // 邮箱
    ga('eleExposureCollectionV1', {
      str1: 'KucoinWeb',
      str2: 'KucoinWeb.login',
      str3: 'KucoinWeb.login.SwitchButton',
      str4: 'KucoinWeb.login.SwitchButton.2',
    });
    // 扫码
    ga('eleExposureCollectionV1', {
      str1: 'KucoinWeb',
      str2: 'KucoinWeb.login',
      str3: 'KucoinWeb.login.SwitchButton',
      str4: 'KucoinWeb.login.SwitchButton.3',
    });
  };

  let comp;

  if (isShowMailAuthorizePage) {
    comp = <MailAuthorize onSuccess={onSuccess} trackingConfig={trackingConfig} />;
  } else if (needValidate) {
    comp = (
      <ValidateForm
        classes={validateFormClasses}
        showLoginSafeWord={showLoginSafeWord}
        onSuccess={onSuccess}
        verifyCanNotUseClick={verifyCanNotUseClick}
        isSub={isSub}
        trackingConfig={trackingConfig}
      />
    );
  } else {
    comp = (
      <RelLogin
        classes={relLoginClasses}
        loginKey={loginKey}
        onSuccess={onSuccess}
        onForgetPwdClick={onForgetPwdClick}
        signOrDownClick={signOrDownClick}
        trackingConfig={trackingConfig}
        theme={theme}
        customTitle={customTitle}
      />
    );
  }
  return (
    <Box style={{ flex: !rv.md ? 1 : 'auto', maxWidth: rv.md ? 480 : '100%' }} {...BoxProps}>
      {comp}
    </Box>
  );
};

const LoginWithTheme = (props = {}) => {
  return (
    <ThemeProvider theme={props?.theme || 'light'}>
      <Login {...props} />
    </ThemeProvider>
  );
};

const LayoutWrapperLogin = withLayout(LoginWithTheme);

const LoginDrawerWithTheme = (props) => {
  const {
    onSuccess = _.noop,
    onForgetPwdClick = _.noop,
    verifyCanNotUseClick,
    BoxProps,
    showLoginSafeWord,
    signOrDownClick,
    loginKey,
    relLoginClasses,
    validateFormClasses,
    trackingConfig = {},
    theme,
    open, // 兼容老API
    show, // 新API
    ...restProps
  } = props;
  const dispatch = useDispatch();

  const handleClose = () => {
    dispatch({
      type: `${NAMESPACE}/reset`,
    });
    if (restProps.onClose) {
      restProps.onClose();
    }
  };

  return (
    <Drawer
      show={open || show}
      {...restProps}
      onClose={handleClose}
      style={{ maxWidth: 480, width: '100%' }}
    >
      <Box
        style={{
          position: 'absolute',
          top: '24px',
          right: '24px',
        }}
        onClick={handleClose}
      >
        <Box
          style={{
            width: '48px',
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '100%',
            cursor: 'pointer',
          }}
        >
          <CloseOutlined size={24} />
        </Box>
      </Box>
      <Login
        loginKey={loginKey}
        showLoginSafeWord={showLoginSafeWord}
        onSuccess={onSuccess}
        onForgetPwdClick={onForgetPwdClick}
        verifyCanNotUseClick={verifyCanNotUseClick}
        signOrDownClick={signOrDownClick}
        relLoginClasses={relLoginClasses}
        validateFormClasses={validateFormClasses}
        BoxProps={{ width: '100%', p: 8, pb: 5, height: '100%', ...BoxProps }}
        trackingConfig={trackingConfig}
        theme={theme}
      />
    </Drawer>
  );
};

export const LoginDrawer = (props) => (
  <ThemeProvider theme={props?.theme || 'light'}>
    <LoginDrawerWithTheme {...props} />
  </ThemeProvider>
);

export const LoginNoLayout = (props) => {
  return <Login {...props} />;
};

export default LayoutWrapperLogin;
